import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Flame, Dumbbell, Trophy, TrendingUp, Loader2, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { BADGE_META } from "@/lib/badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

export function UserProfileCard({ userId, open, onClose }) {
  const { lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const id = lang === "id";
  const isSelf = userId === user?.user_id;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["profile-card", userId],
    queryFn: async () => (await api.get(`/users/${userId}/card`)).data,
    enabled: open && !!userId,
    retry: false,
  });

  const startDm = () => {
    onClose();
    navigate("/chat", { state: { openDm: { user_id: data.user_id, name: data.name, picture: data.picture } } });
  };

  const stats = [
    { icon: Flame, label: id ? "Streak" : "Streak", value: `${data?.workout_streak ?? 0}`, sub: id ? "hari" : "days", cls: "bg-orange-500/15 text-orange-500" },
    { icon: Dumbbell, label: id ? "Total Sesi" : "Sessions", value: `${data?.total_sessions ?? 0}`, sub: id ? "selesai" : "done", cls: "bg-primary/15 text-primary" },
    { icon: TrendingUp, label: id ? "Streak Terbaik" : "Best Streak", value: `${data?.best_streak ?? 0}`, sub: id ? "hari" : "days", cls: "bg-sky-500/15 text-sky-500" },
    { icon: Trophy, label: "PR", value: `${data?.total_prs ?? 0}`, sub: id ? "rekor" : "records", cls: "bg-warning/15 text-warning" },
  ];
  const earned = (data?.badges || []).filter((b) => b.earned);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="profile-card-dialog">
        <DialogHeader><DialogTitle className="sr-only">{data?.name || "Profil"}</DialogTitle></DialogHeader>
        {isLoading || !data ? (
          <div className="flex flex-col items-center gap-3 py-12 text-sm text-muted-foreground">
            {isError ? (id ? "Kartu profil hanya bisa dilihat untuk teman." : "Profile card is only visible for friends.")
              : <Loader2 className="h-6 w-6 animate-spin text-primary" />}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-col items-center text-center">
              <Avatar className="h-20 w-20 ring-2 ring-primary/20">
                <AvatarImage src={data.picture} className="object-cover" />
                <AvatarFallback className="bg-primary/15 text-lg font-semibold text-primary">{initials(data.name)}</AvatarFallback>
              </Avatar>
              <h3 className="mt-3 font-display text-xl font-bold" data-testid="profile-card-name">{data.name}</h3>
              {data.username && <div className="text-sm text-primary">@{data.username}</div>}
              <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={cn("h-2 w-2 rounded-full", data.online ? "bg-emerald-500" : "bg-muted-foreground/40")} />
                {data.online ? (id ? "Online" : "Online") : (id ? "Offline" : "Offline")}
                {data.gym_location && <span>· {data.gym_location}</span>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {stats.map((s, i) => (
                <div key={i} className="rounded-2xl border bg-card p-4">
                  <span className={cn("grid h-8 w-8 place-items-center rounded-lg", s.cls)}><s.icon className="h-4 w-4" /></span>
                  <div className="mt-2 font-display text-2xl font-bold tabular-nums">{s.value}<span className="ml-1 text-xs font-normal text-muted-foreground">{s.sub}</span></div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>

            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {id ? "Lencana" : "Badges"} ({earned.length})
              </div>
              {earned.length ? (
                <div className="flex flex-wrap gap-2" data-testid="profile-card-badges">
                  {earned.map((b) => {
                    const meta = BADGE_META[b.key];
                    const BadgeIcon = (meta && Icons[meta.icon]) || Trophy;
                    const label = meta ? meta[id ? "id" : "en"].t : b.key;
                    return (
                      <span key={b.key} title={meta ? meta[id ? "id" : "en"].d : ""}
                        className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
                        <BadgeIcon className="h-3.5 w-3.5" /> {label}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{id ? "Belum ada lencana." : "No badges yet."}</p>
              )}
            </div>

            {!isSelf && (
              <Button onClick={startDm} className="w-full gap-2" data-testid="profile-card-dm-btn">
                <MessageCircle className="h-4 w-4" /> {id ? "Kirim Pesan" : "Send Message"}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
